<template>
  <div
    ref="page"
    class="task-desk"
    :class="{ 'task-desk--mobile': isMobile, 'task-desk--dark': $vuetify.theme.isDark }"
  >
    <desk-mobile v-if="isMobile" />
    <template v-else>
      <desk-toolbar />
      <div class="task-desk__body">
        <div v-if="!template" class="task-desk__empty">
          <v-icon size="40" color="grey lighten-1">mdi-view-column-outline</v-icon>
          <span>{{ $t('taskDeskSelectTemplate') }}</span>
        </div>
        <component :is="viewComponent" v-else />
      </div>
    </template>

    <!-- Task form (create / edit / copy): local redesigned copy of task.vue -->
    <v-dialog
      v-model="innerComponent"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      :transition="isMobile ? 'dialog-bottom-transition' : 'dialog-transition'"
      width="1100"
    >
      <div v-if="innerComponent" class="td-form" :class="{ 'td-form--mobile': isMobile }">
        <task-form
          :key="formKey"
          :id-prop="dialogData && dialogData.id"
          :copy="dialogData && dialogData.copy"
          :current-status="dialogData && dialogData.customStatus"
          :current-board="dialogData && dialogData.board"
          :dialog-status="innerComponent"
          @close="innerComponent = false"
          @fetchBoard="onBoardFetch"
        />
      </div>
    </v-dialog>

    <v-dialog
      v-if="!isMobile"
      v-model="filterOpen"
      width="760"
      content-class="td-filter-dialog"
    >
      <div class="td-filter">
        <div class="td-filter__head">
          <v-icon small class="me-2">mdi-filter-variant</v-icon>
          <span>{{ $t('filter') }}</span>
          <v-spacer />
          <button type="button" class="td-icon-btn" @click="filterOpen = false">
            <v-icon small>mdi-close</v-icon>
          </button>
        </div>
        <div class="td-filter__body">
          <desk-filters />
        </div>
        <div class="td-filter__foot">
          <v-btn color="primary" depressed height="36" @click="applyFilters">
            {{ $t('taskDeskApply') }}
          </v-btn>
          <v-btn text height="36" :disabled="!activeFilterCount" @click="clearFilters">
            {{ $t('taskDeskClearFilters') }}
          </v-btn>
          <v-spacer />
          <v-btn text height="36" color="grey darken-1" @click="filterOpen = false">
            {{ $t('cancel') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="deleteDialog" width="380" :fullscreen="false">
      <div class="td-confirm">
        <v-icon size="40" color="pink">mdi-trash-can-outline</v-icon>
        <p class="td-confirm__text">{{ $t('are-you-sure-delete-task') }}</p>
        <p v-if="deleteItem" class="td-confirm__name">{{ deleteItem.title }}</p>
        <div class="td-confirm__actions">
          <v-btn color="pink" dark depressed :loading="loading" @click="remove">
            {{ $t('remove') }}
          </v-btn>
          <v-btn text @click="deleteDialog = false">{{ $t('cancel') }}</v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
// Task desk (/taskDesk). Same API calls and payloads as the legacy
// widgets/Board.vue (kept untouched); this page adds board / list / gantt /
// calendar views and a separate mobile UI. Child views reach the state and
// actions through `inject: ['desk']`.
import General from '@/Mixins/general'
import { parseDate } from './deskDates'
import DeskToolbar from './components/DeskToolbar'
import DeskFilters from './components/DeskFilters'
import { claimBack } from './backGuard'

const VIEWS = ['board', 'list', 'gantt', 'calendar']

export default {
  name: 'taskDesk',
  components: {
    DeskToolbar,
    DeskFilters,
    DeskMobile: () => import('./components/DeskMobile'),
    BoardView: () => import('./components/BoardView'),
    ListView: () => import('./components/ListView'),
    GanttView: () => import('./components/GanttView'),
    CalendarView: () => import('./components/CalendarView'),
    TaskForm: () => import('./components/TaskForm'),
  },
  mixins: [General],
  provide () {
    return { desk: this }
  },
  data () {
    return {
      views: VIEWS,
      view: 'board',
      items: [],
      template: null,
      search: null,
      isLoading: false,
      loading: false,
      timer: null,
      doneId: null,
      // filters (same names / params as the legacy board)
      taskStatus: null,
      tag: null,
      functor: null,
      registerar: null,
      participants: null,
      fromDate: null,
      toDate: null,
      fromDateUpdate: null,
      toDateUpdate: null,
      fromDueDate: null,
      toDueDate: null,
      filterOpen: false,
      // dialogs
      innerComponent: null,
      dialogData: null,
      formKey: 0,
      deleteDialog: false,
      deleteItem: null,
    }
  },
  computed: {
    viewComponent () {
      return this.view + '-view'
    },
    jalali () {
      const l = this.$store.state.date_locale || this.$i18n.locale
      return String(l).indexOf('fa') === 0
    },
    columns () {
      return this.items || []
    },
    // Flat list of loaded tasks with parsed dates, used by list/gantt/calendar.
    tasks () {
      const out = []
      this.columns.forEach(col => {
        (col.list || []).forEach(el => {
          if (!el || !(el.id > 0) || !el.ref) return
          let start = parseDate(el.ref.due_date)
          let end = parseDate(el.ref.due_end_date)
          if (start && !end) end = start
          if (end && !start) start = end
          if (start && end && end.isBefore(start)) end = start
          out.push({ key: el.ref.id, el, col, start, end, done: this.isDoneCol(col) })
        })
      })
      return out
    },
    totalTasks () {
      return this.columns.reduce((s, c) => s + (Number(c.total) || 0), 0)
    },
    activeFilterCount () {
      const list = [
        this.taskStatus, this.tag, this.functor, this.registerar, this.participants,
        this.fromDate, this.toDate, this.fromDateUpdate, this.toDateUpdate,
        this.fromDueDate, this.toDueDate,
      ]
      return list.filter(v => (Array.isArray(v) ? v.length : v)).length
    },
  },
  watch: {
    template () {
      if (this.template) this.getItems()
      localStorage.setItem('taskTemplate', JSON.stringify(this.template))
    },
    view (val) {
      try { localStorage.setItem('taskDeskView', val) } catch (e) {}
    },
    items (val) {
      if (val && val.length && !this.doneId) {
        const k = val.findIndex(x => x.state === 'done')
        if (k > -1) this.doneId = val[k].id
      }
    },
    search () {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.getItems, 700)
    },
    isMobile () {
      this.$nextTick(this.measureSoon)
    },
    // Remount the form on every open; the back button closes it.
    innerComponent (open) {
      if (open) {
        this.formKey++
        // Any width: phones in landscape can be wider than the isMobile cutoff.
        this.releaseForm = claimBack('tdForm', () => { this.innerComponent = false })
      } else if (this.releaseForm) {
        this.releaseForm()
        this.releaseForm = null
      }
    },
  },
  created () {
    // Before children mount, so the template picker's set-init does not
    // override the saved board.
    try {
      const template = localStorage.getItem('taskTemplate')
      if (template && template !== 'undefined') this.template = JSON.parse(template)
      const view = localStorage.getItem('taskDeskView')
      if (VIEWS.indexOf(view) > -1) this.view = view
    } catch (e) {}
  },
  mounted () {
    this.measureSoon()
    window.addEventListener('resize', this.measure)
  },
  activated () {
    this.measureSoon()
    if (this.template && this.wasDeactivated) this.getItems()
  },
  deactivated () {
    this.wasDeactivated = true
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.measure)
    clearTimeout(this.timer)
  },
  methods: {
    measureSoon () {
      this.measure()
      setTimeout(this.measure, 350)
    },
    // Fill the viewport below the app bar; views scroll inside.
    measure () {
      const el = this.$refs.page
      if (!el) return
      const top = el.getBoundingClientRect().top
      const gap = this.isMobile ? 0 : 8
      el.style.height = Math.max(360, window.innerHeight - top - gap) + 'px'
    },
    isDoneCol (col) {
      return !!col && (col.state === 'done' || col.id === this.doneId)
    },
    filterParams () {
      return {
        template_id: this.template ? this.template.id : null,
        taskStatus: this.taskStatus,
        tag: this.tag,
        search: this.search,
        toDueDate: this.toDueDate,
        fromDueDate: this.fromDueDate,
        toDateUpdate: this.toDateUpdate,
        fromDateUpdate: this.fromDateUpdate,
        fromDate: this.fromDate,
        functor: this.functor,
        toDate: this.toDate,
      }
    },
    getItems () {
      if (!this.template) return
      const app = this
      app.isLoading = true
      app.filterOpen = false
      app.$axios
        .get(this.$endpoint + 'status/show', {
          params: {
            ...app.filterParams(),
            board: 'yes',
            type: 'sell',
            user_id: app.registerar,
            participants: app.participants,
          },
        })
        .then(res => {
          const items = res.data || []
          items.forEach(i => {
            app.$set(i, 'descVisibility', false)
            if (!('description' in i)) app.$set(i, 'description', null)
          })
          app.items = items
        })
        .catch(err => app.handleError(err))
        .finally(() => (app.isLoading = false))
    },
    // Next page of one status column.
    getTask (item) {
      const app = this
      if (item.page + 1 > item.lastPage) {
        setTimeout(() => { item.loader = false }, 1000)
        return false
      }
      app.isLoading = true
      item.page = parseInt(item.page) + 1
      app.$axios
        .get(this.$endpoint + 'task', {
          params: {
            ...app.filterParams(),
            getTaskBoard: true,
            status_id: item.id,
            page: item.page,
            user_id: app.registerar,
            participants: app.participants,
          },
        })
        .then(res => {
          item.list = [...item.list.filter(x => x.id > 0), ...Array.from(res.data.data)]
        })
        .catch(err => app.handleError(err))
        .finally(() => (app.isLoading = false))
    },
    hasMore (col) {
      return col.lastPage > 1 && col.page < col.lastPage && col.loader !== false
    },
    handleError (err) {
      const error = err && err.response
      if (error && error.status === 401) this.$router.push({ name: 'logout' })
    },
    applyFilters () {
      this.getItems()
    },
    clearFilters () {
      this.taskStatus = null
      this.tag = null
      this.functor = null
      this.registerar = null
      this.participants = null
      this.fromDate = null
      this.toDate = null
      this.fromDateUpdate = null
      this.toDateUpdate = null
      this.fromDueDate = null
      this.toDueDate = null
      this.getItems()
    },
    // ---- task actions (same behaviour as the legacy board) ----
    editTask (component, item) {
      this.dialogData = { name: component, id: item.id }
      this.innerComponent = true
    },
    copyTask (component, item) {
      this.dialogData = { name: component, id: item.id, copy: true }
      this.innerComponent = true
    },
    openComponent (component, id) {
      this.dialogData = { name: component, id: id }
      this.innerComponent = true
    },
    openCreateTask (component, id, currentStatus, template) {
      this.dialogData = {
        name: component,
        id: id,
        customStatus: currentStatus,
        board: template || this.template,
      }
      this.innerComponent = true
    },
    share (elm) {
      const url = '' + this.$HostName + '/task/edit/' + elm.id
      if (navigator.share) {
        navigator.share({ title: elm.title, text: '', url })
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url)
      }
    },
    openLink (item) {
      window.open(item.ref.link, '_blank')
    },
    openRelationLink (item) {
      if (item.ref.type === 'account') {
        this.$router.push({ name: 'customerEdit', params: { id: item.relation.id } })
      }
      if (item.ref.type === 'invoice') {
        this.$router.push({ name: item.relation.type + 'Edit', params: { id: item.relation.uid } })
      }
    },
    askDelete (item) {
      this.deleteItem = item
      this.deleteDialog = true
    },
    remove () {
      const app = this
      if (!app.deleteItem) return false
      app.loading = true
      app.$axios
        .delete(this.$endpoint + 'task/' + this.deleteItem.id)
        .then(() => {
          app.deleteItem = null
          app.deleteDialog = false
          app.getItems()
        })
        .finally(() => (app.loading = false))
    },
    onBoardFetch (payload) {
      if (payload && payload.mode === 'update' && payload.id) {
        this.updateTaskInPlace(payload)
        return
      }
      this.getItems()
    },
    updateTaskInPlace (payload) {
      let fromCol = null
      let fromIdx = -1
      this.items.forEach(col => {
        const i = (col.list || []).findIndex(el => el.ref && el.ref.id === payload.id)
        if (i > -1) {
          fromCol = col
          fromIdx = i
        }
      })
      if (!fromCol) return

      const card = fromCol.list[fromIdx]
      if (card.ref) {
        card.ref.title = payload.title
        this.$set(card.ref, 'tags', payload.tag)
        this.$set(card.ref, 'functor', payload.functor)
        this.$set(card.ref, 'participants', payload.participants)
        this.$set(card.ref, 'due_end_date', payload.dueDate)
        this.$set(card.ref, 'link', payload.link)
      }
      this.$set(card, 'relation', payload.relation)

      const newStatusId = payload.status && payload.status.id
      if (!newStatusId || newStatusId === fromCol.id) return
      const toCol = this.items.find(c => c.id === newStatusId)
      if (!toCol) return
      this.moveLocal(card, fromCol, toCol)
    },
    moveLocal (card, fromCol, toCol) {
      const idx = fromCol.list.indexOf(card)
      if (idx > -1) fromCol.list.splice(idx, 1)
      if (fromCol.list.length === 0) fromCol.list.push({ name: this.$t('clear-item-here'), id: 0 })
      if (fromCol.total > 0) fromCol.total--
      const emptyIdx = (toCol.list || []).findIndex(x => x.id === 0)
      if (emptyIdx > -1) toCol.list.splice(emptyIdx, 1)
      card.status_id = toCol.id
      toCol.list.unshift(card)
      toCol.total = (toCol.total || 0) + 1
    },
    // vuedraggable @change on a column
    change (colId, eve) {
      const kind = Object.keys(eve)[0]
      const col = this.items.find(x => x.id === colId)
      if (!col) return
      if (kind === 'added') {
        const j = col.list.findIndex(x => x.id === 0)
        if (j > -1) col.list.splice(j, 1)
        const el = eve.added.element
        this.changeStatus(colId, el.type, el.ref_id, el)
      }
      if (kind === 'removed' && col.list.length === 0) {
        col.list.push({ name: this.$t('clear-item-here'), id: 0 })
      }
      if (kind === 'moved') this.changeSort(col.list)
    },
    changeStatus (statusId, type, refId, elm) {
      const app = this
      app.isLoading = true
      app.$axios
        .post(this.$endpoint + 'status/change', { id: refId, type: 'task', status: statusId })
        .then(() => {
          elm.status_id = statusId
          app.getItems()
        })
        .catch(err => app.handleError(err))
        .finally(() => (app.isLoading = false))
    },
    // Move from list / gantt / mobile (no drag): optimistic, then persist.
    moveTo (task, toCol) {
      if (!task || !toCol || task.col.id === toCol.id) return
      this.moveLocal(task.el, task.col, toCol)
      this.changeStatus(toCol.id, task.el.type, task.el.ref_id || task.el.ref.id, task.el)
    },
    changeSort (list) {
      const app = this
      app.isLoading = true
      app.$axios
        .post(this.$endpoint + 'status/sort', { list: list })
        .catch(err => app.handleError(err))
        .finally(() => (app.isLoading = false))
    },
    setDoneStatus (item) {
      const k = this.items.findIndex(x => x.state === 'done')
      if (k < 0) return
      this.changeStatus(this.items[k].id, item.type, item.id, item)
    },
    openDescColumn (item) {
      this.items.forEach(i => (i.descVisibility = false))
      item.descVisibility = true
    },
    // Quick add from a column footer (title only).
    submitTaskPreview (item, e) {
      if (e && e.shiftKey) return
      if (e && e.preventDefault) e.preventDefault()
      if (!item.description || !String(item.description).trim()) return false
      const app = this
      app.isLoading = true
      app.$axios
        .post(this.$endpoint + 'task', {
          status: {
            type: item.type,
            id: item.id,
            name: item.name,
            state: item.state,
            template_id: item.template_id,
            color: item.color,
          },
          title: item.description,
        })
        .then(() => {
          item.description = null
          item.descVisibility = false
          app.getItems()
        })
        .catch(err => this.serverErrorHandeler(err))
        .finally(() => (app.isLoading = false))
    },
  },
}
</script>

<style scoped>
.task-desk {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin: 4px 0 0;
  padding: 12px 14px 12px;
  background: #f3f5f9;
  border-radius: 5px;
  overflow: hidden;
}
.task-desk--dark {
  background: #121212;
}
.task-desk--mobile {
  margin: 0;
  padding: 0;
  border-radius: 0;
}
/* A global rule pins .v-input to 20px; restore Vuetify's default here only. */
.task-desk ::v-deep .v-input {
  height: auto !important;
}
.task-desk__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.task-desk__empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #8a94a6;
}
.td-form {
  height: min(88vh, 860px);
  border-radius: 8px;
  overflow: hidden;
}
.td-form--mobile {
  height: 100%;
  border-radius: 0;
}
.td-confirm {
  background: #fff;
  border-radius: 8px;
  padding: 24px 20px 16px;
  text-align: center;
}
.theme--dark .td-form {
  height: min(88vh, 860px);
  border-radius: 8px;
  overflow: hidden;
}
.td-form--mobile {
  height: 100%;
  border-radius: 0;
}
.td-confirm {
  background: #1e1e1e;
}
.td-confirm__text {
  margin: 12px 0 4px;
  font-weight: 600;
}
.td-confirm__name {
  color: #6b7280;
  font-size: 0.85rem;
  margin: 0;
}
.td-confirm__actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
}
.td-filter {
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  background: #fff;
}
.theme--dark .td-filter {
  background: #1e1e1e;
}
.td-filter__head,
.td-filter__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
}
.td-filter__head {
  font-weight: 700;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.td-filter__foot {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.td-filter__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
}
.td-filter ::v-deep .v-input {
  height: auto !important;
}
.td-filter .td-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.td-filter .td-icon-btn:hover {
  background: rgba(0, 0, 0, 0.06);
}
</style>
